/**
 * Activity types dashboard section: list with create/edit slide panel.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ActivityTypesDataTable } from '@/components/activity-types/ActivityTypesDataTable';
import { SlidePanel } from '@/components/common/SlidePanel';
import { CreateActivityTypeView } from '@/components/activity-types/CreateActivityTypeView';
import { EditActivityTypeView } from '@/components/activity-types/EditActivityTypeView';
import { useDetailsView } from '@/hooks/useDetailsView';
import { usePageActions } from '@/hooks/usePageActions';
import type { ElementWidth } from '@/types/interface-elements.types';

/** Props for {@link ActivityTypesSection}. */
interface ActivityTypesSectionProps {
  layoutWidth?: ElementWidth;
  onSuccess?: () => void;
}

/** Dashboard widget: activity types list with create/edit. */
export const ActivityTypesSection: React.FC<ActivityTypesSectionProps> = ({
  layoutWidth = 'full',
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  const { canCreate, canEditRow, canDeleteRow, canExportCsv } = usePageActions('activityTypes');
  const { openRowId, openRow, closeRow } = useDetailsView();
  const [createFormOpen, setCreateFormOpen] = React.useState(false);
  const [editActivityTypeId, setEditActivityTypeId] = React.useState<string | null>(null);

  const handleCreateActivityType = () => {
    setCreateFormOpen(true);
    setEditActivityTypeId(null);
    closeRow();
  };

  const handleEditActivityType = (id: string) => {
    setEditActivityTypeId(id);
    setCreateFormOpen(false);
    closeRow();
  };

  const closeForm = () => {
    setCreateFormOpen(false);
    setEditActivityTypeId(null);
  };

  const handleSuccess = () => {
    closeForm();
    void queryClient.invalidateQueries({ queryKey: ['activityTypes'] });
    onSuccess?.();
  };

  return (
    <>
      <ActivityTypesDataTable
        isSection={true}
        showCreateButton={canCreate}
        onCreateActivityType={handleCreateActivityType}
        layoutWidth={layoutWidth}
        expandedRowId={openRowId}
        onToggleRow={openRow}
        onCloseDetails={closeRow}
        onEditActivityType={handleEditActivityType}
        pageActions={{ canEditRow, canDeleteRow, canExportCsv }}
      />

      <SlidePanel isOpen={createFormOpen} onClose={closeForm}>
        <CreateActivityTypeView onClose={closeForm} onSuccess={handleSuccess} />
      </SlidePanel>

      <SlidePanel isOpen={editActivityTypeId !== null} onClose={closeForm}>
        {editActivityTypeId && (
          <EditActivityTypeView
            key={editActivityTypeId}
            activityTypeId={editActivityTypeId}
            onClose={closeForm}
            onSuccess={handleSuccess}
          />
        )}
      </SlidePanel>
    </>
  );
};
